import React, { useState, useEffect } from 'react';
import MultiSelect from '../../../ClassSearch/MultiSelect';
import { useAuth } from '../../../../contexts/AuthContext';

const PERSISTENCE_CATEGORIES = ['Very High', 'High', 'Moderate', 'Low', 'Very Low'];

// Example student fields; replace with your actual fields or fetch from context/props
const studentFields = ['Planned', 'Taken', 'Total', 'GPA'];

const NewSearchComponent = ({ onSearch, advisors }) => {
  const { fetchedMajors } = useAuth();

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGradYears, setSelectedGradYears] = useState([]);
  const [selectedPrograms, setSelectedPrograms] = useState([]);
  const [selectedAdvisors, setSelectedAdvisors] = useState([]);
  const [selectedPersistence, setSelectedPersistence] = useState([]);

  // Custom field-based filter
  const [selectedField, setSelectedField] = useState('');
  const [selectedOperator, setSelectedOperator] = useState('');
  const [filterValue, setFilterValue] = useState('');

  // Available filters
  const [availableFilters, setAvailableFilters] = useState({
    gradYear: [],
    program: [],
    advisors: [],
    persistence: PERSISTENCE_CATEGORIES
  });

  useEffect(() => {
    // Fill from your context or other data sources
    const newAvailable = {
      gradYear: ["2025", "2026", "2027", "2028"], // Possibly fill from your data if needed
      program: Object.keys(fetchedMajors || {}),
      advisors: advisors || [],
      persistence: PERSISTENCE_CATEGORIES
    };
    setAvailableFilters(newAvailable);
  }, [fetchedMajors, advisors]);

  const handleSearchClick = () => {
    const newFilters = {
      searchTerm,
      gradYear: selectedGradYears,
      program: selectedPrograms,
      advisors: selectedAdvisors,
      persistence: selectedPersistence,
      customField: {
        field: selectedField,
        operator: selectedOperator,
        value: filterValue
      }
    };
    onSearch(newFilters);
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setSelectedGradYears([]);
    setSelectedPrograms([]);
    setSelectedAdvisors([]);
    setSelectedPersistence([]);
    setSelectedField('');
    setSelectedOperator('');
    setFilterValue('');

    // Also tell parent to reset to show all
    onSearch({
      searchTerm: '',
      gradYear: [],
      program: [],
      advisors: [],
      persistence: [],
      customField: { field: '', operator: '', value: '' }
    });
  };

  return (
    <div className="p-4 bg-gray-50 shadow-md border rounded-lg">
      <h5 className="text-lg text-center font-bold mb-2">Filters</h5>

      {/* Search by Name */}
      <div className="mb-2">
        <label className="block text-sm font-medium mb-1">Search by Name:</label>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="form-input mt-1 block w-full rounded-md border-gray-300"
        />
      </div>

      {/* Custom Field Filter */}
      <div className="mb-2">
        <label className="block text-sm font-medium mb-1">Custom Field:</label>
        <MultiSelect
          suggestions={studentFields}
          value={selectedField ? [selectedField] : []}
          onChange={(e) => {
            const newField = e.target.value[0] || '';
            setSelectedField(newField);
            setSelectedOperator('');
            setFilterValue('');
          }}
          placeholder="Select a student field"
          singleSelect
          tagColor="gray"
        />
      </div>

      {/* Operator (only show if a custom field is selected) */}
      {selectedField && (
        <div className="mb-2">
          <label className="block text-sm font-medium mb-1">Operator:</label>
          <MultiSelect
            suggestions={['equals', 'greater than', 'less than']}
            value={selectedOperator ? [selectedOperator] : []}
            onChange={(e) => {
              const op = e.target.value[0] || '';
              setSelectedOperator(op);
              setFilterValue('');
            }}
            placeholder="Select an operator"
            singleSelect
            tagColor="gray"
          />
        </div>
      )}

      {/* Value (only show if both field and operator are selected) */}
      {selectedField && selectedOperator && (
        <div className="mb-2">
          <label className="block text-sm font-medium mb-1">Value:</label>
          <input
            type="text"
            placeholder="Enter a value"
            value={filterValue}
            onChange={(e) => setFilterValue(e.target.value)}
            className="form-input mt-1 block w-full rounded-md border-gray-300"
          />
        </div>
      )}

      {/* Grad Year */}
      <div className="mb-2">
        <label className="block text-sm font-medium mb-1">Graduation Year:</label>
        <MultiSelect
          suggestions={availableFilters.gradYear}
          value={selectedGradYears}
          onChange={(e) => setSelectedGradYears(e.target.value)}
          placeholder="Select grad years"
          required={false}
          tagColor="red"
        />
      </div>

      {/* Program */}
      <div className="mb-2">
        <label className="block text-sm font-medium mb-1">Program:</label>
        <MultiSelect
          suggestions={availableFilters.program}
          value={selectedPrograms}
          onChange={(e) => setSelectedPrograms(e.target.value)}
          placeholder="Select programs"
          required={false}
          tagColor="green"
        />
      </div>

      {/* Advisor */}
      <div className="mb-2">
        <label className="block text-sm font-medium mb-1">Advisor:</label>
        <MultiSelect
          suggestions={availableFilters.advisors}
          value={selectedAdvisors}
          onChange={(e) => setSelectedAdvisors(e.target.value)}
          placeholder="Select advisors"
          required={false}
          tagColor="purple"
        />
      </div>

      {/* Persistence */}
      <div className="mb-2">
        <label className="block text-sm font-medium mb-1">Persistence:</label>
        <MultiSelect
          suggestions={availableFilters.persistence}
          value={selectedPersistence}
          onChange={(e) => setSelectedPersistence(e.target.value)}
          placeholder="Select persistence levels"
          required={false}
          tagColor="orange"
        />
      </div>

      {/* Buttons */}
      <button
        className="w-full bg-blue-500 hover:bg-blue-700 text-white text-sm py-2 px-4 rounded mt-2"
        onClick={handleSearchClick}
      >
        Search
      </button>
      <button
        className="w-full bg-gray-400 hover:bg-gray-500 text-white text-sm py-2 px-4 rounded mt-2"
        onClick={handleResetFilters}
      >
        Reset
      </button>
    </div>
  );
};

export default NewSearchComponent;
